import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Loader2, FileText, Check, AlertCircle, Code, Eye, Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Heading1, Heading2, Heading3 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import UnderlineExtension from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

interface LegalDocument {
  id: number;
  slug: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

const DOCUMENT_TABS = [
  { slug: 'terms', label: 'Пользовательское соглашение' },
  { slug: 'privacy', label: 'Политика конфиденциальности' },
  { slug: 'offer', label: 'Оферта' },
  { slug: 'consent', label: 'Согласие на обработку ПД' },
  { slug: 'cookies', label: 'Cookie' },
];

// Toolbar button component
const ToolbarButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}> = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded transition-colors ${
      isActive
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:bg-gray-100'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

// Editor toolbar
const EditorToolbar: React.FC<{ editor: any }> = ({ editor }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL ссылки:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50">
      <div className="flex gap-1 pr-2 border-r border-gray-300">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Заголовок 1"
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Заголовок 2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Заголовок 3"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <div className="flex gap-1 px-2 border-r border-gray-300">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Жирный (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Курсив (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Подчёркнутый (Ctrl+U)"
        >
          <Underline className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <div className="flex gap-1 px-2 border-r border-gray-300">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Маркированный список"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Нумерованный список"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <div className="flex gap-1 pl-2">
        <ToolbarButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Добавить ссылку"
        >
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
      </div>
    </div>
  );
};

const DocumentsEditor: React.FC = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [activeTab, setActiveTab] = useState('terms');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedContent, setEditedContent] = useState<{ [key: string]: string }>({});
  const [hasChanges, setHasChanges] = useState<{ [key: string]: boolean }>({});
  const [viewMode, setViewMode] = useState<'visual' | 'html'>('visual');
  
  // Use ref to track if we're programmatically setting content
  const isSettingContentRef = useRef(false);
  const baselineContentRef = useRef<{ [key: string]: string }>({});

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:underline',
        },
      }),
      UnderlineExtension,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse w-full',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-4 min-h-[500px] focus:outline-none',
      },
    },
    onUpdate: ({ editor, transaction }) => {
      // Ignore programmatic content changes
      if (isSettingContentRef.current) return;
      
      // Ignore non-document changes
      if (!transaction.docChanged) return;
      
      const html = editor.getHTML();
      
      // Get current tab from closure - need to use a workaround
      const currentTab = document.querySelector('[data-active-tab]')?.getAttribute('data-active-tab') || 'terms';
      
      setEditedContent(prev => ({ ...prev, [currentTab]: html }));
      
      // Mark as changed only if different from baseline
      const baseline = baselineContentRef.current[currentTab];
      if (baseline !== undefined && html !== baseline) {
        setHasChanges(prev => ({ ...prev, [currentTab]: true }));
      }
    },
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  // Update editor content when switching tabs
  useEffect(() => {
    if (editor && editedContent[activeTab] !== undefined) {
      isSettingContentRef.current = true;
      editor.commands.setContent(editedContent[activeTab] || '');
      
      // Save baseline after TipTap normalizes
      requestAnimationFrame(() => {
        if (!baselineContentRef.current[activeTab]) {
          baselineContentRef.current[activeTab] = editor.getHTML();
        }
        isSettingContentRef.current = false;
      });
    }
  }, [activeTab, editor, editedContent]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/legal-documents');
      const docs = response.data;
      setDocuments(docs);
      
      const initialContent: { [key: string]: string } = {};
      docs.forEach((doc: LegalDocument) => {
        initialContent[doc.slug] = doc.content;
      });
      setEditedContent(initialContent);
      baselineContentRef.current = {};
      setHasChanges({});
      
      // Set initial content for the first tab
      if (editor && initialContent['terms']) {
        isSettingContentRef.current = true;
        editor.commands.setContent(initialContent['terms']);
        requestAnimationFrame(() => {
          baselineContentRef.current['terms'] = editor.getHTML();
          isSettingContentRef.current = false;
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки документов:', error);
      toast.error('Ошибка загрузки документов');
    } finally {
      setLoading(false);
    }
  };

  const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    setEditedContent(prev => ({ ...prev, [activeTab]: content }));
    
    // Mark as changed
    const baseline = baselineContentRef.current[activeTab];
    setHasChanges(prev => ({
      ...prev,
      [activeTab]: content !== baseline
    }));
  };

  const switchToVisualMode = () => {
    setViewMode('visual');
    if (editor) {
      isSettingContentRef.current = true;
      editor.commands.setContent(editedContent[activeTab] || '');
      requestAnimationFrame(() => {
        isSettingContentRef.current = false;
      });
    }
  };

  const handleSave = async (slug: string) => {
    try {
      setSaving(true);
      
      const content = editedContent[slug];
      const doc = documents.find(d => d.slug === slug);
      
      await api.put(`/admin/legal-documents/${slug}`, {
        title: doc?.title,
        content
      });
      
      setDocuments(prev => prev.map(d => 
        d.slug === slug ? { ...d, content, updated_at: new Date().toISOString() } : d
      ));
      
      // Update baseline after save
      if (editor && slug === activeTab) {
        baselineContentRef.current[slug] = editor.getHTML();
      } else {
        baselineContentRef.current[slug] = content;
      }
      setHasChanges(prev => ({ ...prev, [slug]: false }));
      
      toast.success('Документ успешно сохранен');
    } catch (error) {
      console.error('Ошибка сохранения документа:', error);
      toast.error('Ошибка сохранения документа');
    } finally {
      setSaving(false);
    }
  };

  const activeDocument = documents.find(d => d.slug === activeTab);
  const activeContent = editedContent[activeTab] || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Загрузка документов...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-active-tab={activeTab}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Юридические документы
        </h2>
      </div>

      {/* Document Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
          {DOCUMENT_TABS.map(tab => {
            const isActive = activeTab === tab.slug;
            const hasChange = hasChanges[tab.slug];
            
            return (
              <button
                key={tab.slug}
                onClick={() => setActiveTab(tab.slug)}
                className={`
                  whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors
                  flex items-center gap-2
                  ${isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
                {hasChange && (
                  <span className="w-2 h-2 bg-orange-500 rounded-full" title="Есть несохраненные изменения" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Editor */}
      {activeDocument && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Последнее обновление: {new Date(activeDocument.updated_at).toLocaleString('ru-RU')}
            </div>
            <button
              onClick={() => handleSave(activeTab)}
              disabled={saving || !hasChanges[activeTab]}
              className={`
                flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors
                ${hasChanges[activeTab]
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Сохранение...
                </>
              ) : hasChanges[activeTab] ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Сохранить изменения
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Сохранено
                </>
              )}
            </button>
          </div>

          {hasChanges[activeTab] && (
            <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700">
              <AlertCircle className="h-4 w-4" />
              Есть несохраненные изменения. Не забудьте сохранить перед переключением на другой документ.
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={switchToVisualMode}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === 'visual'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Eye className="h-4 w-4" />
              Визуальный редактор
            </button>
            <button
              onClick={() => setViewMode('html')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === 'html'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Code className="h-4 w-4" />
              HTML код
            </button>
          </div>

          {viewMode === 'visual' ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <EditorToolbar editor={editor} />
              <style>{`
                .ProseMirror ul { list-style-type: disc !important; padding-left: 1.5em !important; }
                .ProseMirror ol { list-style-type: decimal !important; padding-left: 1.5em !important; }
                .ProseMirror li { margin: 0.25em 0; }
                .ProseMirror h1 { font-size: 1.75em; font-weight: bold; margin: 0.67em 0; }
                .ProseMirror h2 { font-size: 1.5em; font-weight: bold; margin: 0.75em 0; }
                .ProseMirror h3 { font-size: 1.25em; font-weight: bold; margin: 0.83em 0; }
                .ProseMirror p { margin: 0.5em 0; }
                .ProseMirror a { color: #2563eb; text-decoration: underline; }
                .ProseMirror table { border-collapse: collapse; width: 100%; margin: 1em 0; table-layout: fixed; }
                .ProseMirror th, .ProseMirror td { border: 1px solid #d1d5db; padding: 0.5em 0.75em; text-align: left; vertical-align: top; }
                .ProseMirror th { background-color: #f3f4f6; font-weight: 600; }
                .ProseMirror tr:nth-child(even) { background-color: #f9fafb; }
              `}</style>
              <EditorContent editor={editor} className="min-h-[500px]" />
            </div>
          ) : (
            <textarea
              value={activeContent}
              onChange={handleHtmlChange}
              className="w-full h-[600px] p-4 border border-gray-200 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Введите HTML содержимое документа..."
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentsEditor;
